// pages/sections/JobReviewSection.js
// Stale Jobs + Late Filings — Manager-only review (one component, two sections;
// `sectionKey` is 'stale-jobs' or 'late-filings'). Lists the caller's open tax
// jobs (admins see firm-wide) with two independent decisions per job:
//   * Rolls to next year — PE's "Recurring" setting (set-recurring). Separate
//     from whether the job is closed now.
//   * Decision — Close now (close) or Still working (still-working: expected
//     completion + reason). Either one completes the row.
// Plus an optional Forward (current staff + work status Filing Late), which does
// NOT complete the row. All decisions are audit-log only; the overnight refresh
// applies them to PE. A manager can change any decision after the row is
// complete (latest wins) until PE has applied it; an admin Unmark reopens it.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import { useSectionData } from '../../hooks/useSectionData';
import { useRowMutations } from '../../hooks/useRowMutations';
import LastRefreshed from '../../components/LastRefreshed';
import ToggleSwitch from './ToggleSwitch';
import DoneCell from './DoneCell';
import MirrorNote from './MirrorNote';
import PeJobLink from './PeJobLink';
import ReviewPagination, { REVIEW_PAGE_SIZE as PAGE_SIZE } from './ReviewPagination';
import { formatMoney } from './clientDataUtil';
import { fmtDate, fmtHours, doneTip, postJson, postAdminUnmark, patchRowIn } from './reviewUtil';
import './ReviewTable.css';
import './JobReviewSection.css';

const NOTE_MAX = 1000;

const CATEGORY_LABELS = {
  return:     'Return',
  estimates:  'Estimates',
  planning:   'Planning',
  amended:    'Amended / 843',
  compliance: 'Other compliance',
  other:      'Other tax work',
};

// Why a job is in Stale Jobs (server: jobsData.staleReasons), with hover help.
// `ty` = the season's tax year (api/shared/jobsData.js LATE_FILING_TAX_YEAR, sent as data.taxYear).
const REASONS = {
  'past-idd':               { label: 'Past IDD',                 tip: () => 'Past its internal due date (IDD) in PE.' },
  'prior-year':             { label: 'Prior year',               tip: ty => `From a tax year before ${ty} and still open.` },
  'not-doing-cy':           { label: 'Not Doing CY',             tip: () => 'Marked Not Doing CY but the job was never closed.' },
  'old-estimates-planning': { label: 'Old estimates / planning', tip: ty => `A ${ty}-or-earlier estimates or planning job that is still open.` },
  'open-past-season':       { label: 'Open past its season',     tip: ty => `A ${ty} job that is still open after its season.` },
};

function reasonChip(code, row, taxYear) {
  const r = REASONS[code] || { label: code, tip: () => '' };
  const days = code === 'past-idd' && row.daysPastIdd != null ? ` by ${row.daysPastIdd}d` : '';
  const base = r.tip(taxYear ?? 'the season’s');
  const tip = code === 'past-idd' && row.idd ? `${base} IDD was ${fmtDay(row.idd)}.` : base;
  return (
    <span key={code} className={`rt-chip jr-reason jr-reason--${code}`} title={tip}>
      {r.label}{days}
    </span>
  );
}

const SUBTITLES = {
  'late-filings':
    'Tax-year 2025 returns past their (extended) due date that aren’t filed yet. For each job: confirm ' +
    'whether it rolls to next year, then either close it or tell us when it will be done and why.',
  'stale-jobs':
    'Open tax jobs that are past their internal due date (IDD) — any year, including 2026 — or from 2025 ' +
    'and earlier and look finished or abandoned (estimates, planning, prior-year returns, Not Doing CY). ' +
    'For each job: confirm whether it rolls to next year, then close it or tell us why it’s still open.',
};

const WIP_EPSILON = 0.01;

// 'YYYY-MM-DD' -> local Date (new Date('YYYY-MM-DD') parses as UTC and shifts a day).
function parseYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s ?? ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

function fmtDay(ymd) {
  const d = parseYmd(ymd);
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : '—';
}

// Today in the firm's timezone, matching the server's "can't be in the past" check.
function todayYmd() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
}

const hasWip = row => Number(row.unbilledWip) > WIP_EPSILON;

// ── Decision cell ────────────────────────────────────────────────────────────
// mode: null (summary, or the two choices when nothing is decided yet),
//       'choose' (Change clicked — two choices again), 'still' (mini-form).
function DecisionCell({ row, reasons, readOnly, onClose, onStill, onUndo }) {
  const review = row.review || {};
  const { disposition } = review;
  const [mode, setMode] = useState(null);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  const disabled = readOnly;
  const reasonLabel = code => (reasons.find(r => r.code === code) || {}).label || code;

  // Hover help for the two choices — says exactly what PE will be asked to do.
  const rollState = row.roll?.state;
  const closeTip = hasWip(row)
    ? `Mark this job Completed (status 2) in PE overnight — it has ${formatMoney(row.unbilledWip, { cents: true })} unbilled WIP, so PE won't let it be Closed until that's billed. Completes this row.`
    : rollState === 'prior-year'
      ? 'Close this job (status 3) in PE overnight. It\'s from a prior year, so it closes without rolling. Completes this row.'
      : 'Close this job (status 3) in PE overnight. Whether that creates next year\'s job follows "Rolls to next year". Completes this row.';
  const stillTip = 'Keep the job open and tell us when you expect it to be done and why (expected completion + reason). Completes this row.';

  function openStill() {
    // Prefill from the recorded decision so a manager can tweak it.
    const wasStill = disposition === 'still-working';
    setDate(wasStill ? (review.expectedCompletion || '') : '');
    setReason(wasStill ? (review.reason || '') : '');
    setNote(wasStill ? (review.reasonNote || '') : '');
    setMode('still');
  }

  // Saved optimistically: the cell shows the decision at once; a failure reverts the row and toasts.
  function handleClose() {
    setMode(null);
    onClose(row);
  }

  function handleSave() {
    const trimmed = note.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !parseYmd(date)) {
      toast.error('Expected completion must be a date.');
      return;
    }
    if (date < todayYmd()) {
      toast.error('Expected completion can’t be in the past.');
      return;
    }
    if (!reasons.some(r => r.code === reason)) {
      toast.error('Pick a reason.');
      return;
    }
    if (reason === 'other' && !trimmed) {
      toast.error('Add a note when the reason is Other.');
      return;
    }
    if (trimmed.length > NOTE_MAX) {
      toast.error(`Note is limited to ${NOTE_MAX} characters.`);
      return;
    }
    setMode(null);
    onStill(row, { expectedCompletion: date, reason, note: trimmed });
  }

  if (mode === 'still') {
    return (
      <div className="jr-form">
        <label className="jr-form-lbl" title="When you expect this job to be finished (today or later)">
          Expected completion
          <input type="date" className="jr-input" value={date} min={todayYmd()}
                 onChange={e => setDate(e.target.value)} />
        </label>
        <label className="jr-form-lbl" title="Why the job is still open / late">
          Reason
          <select className="jr-input" value={reason} onChange={e => setReason(e.target.value)}>
            <option value="">Select a reason…</option>
            {reasons.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </label>
        <textarea className="jr-input jr-note" rows={2} maxLength={NOTE_MAX}
                  placeholder="Note (required for Other)" value={note}
                  title="Optional detail — required when the reason is Other"
                  onChange={e => setNote(e.target.value)} />
        <div className="jr-form-actions">
          <button className="rt-btn rt-btn--primary" disabled={disabled} onClick={handleSave}
                  title="Save the expected completion date and reason. Completes this row.">
            Save
          </button>
          <button className="rt-btn" onClick={() => setMode(null)}
                  title="Discard these changes">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!disposition || mode === 'choose') {
    return (
      <div className="jr-choices">
        <button className="rt-btn jr-btn--still" disabled={disabled} onClick={openStill} title={stillTip}>Still working</button>
        <button className="rt-btn jr-btn--close" disabled={disabled} onClick={handleClose} title={closeTip}>Close now</button>
        {disposition && (
          <button className="rt-link" onClick={() => setMode(null)}
                  title="Keep your current decision">
            cancel
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="jr-decision">
      {disposition === 'close' ? (
        <span className={`jr-decision-tag ${review.closeStatus === 2 ? 'jr-decision-tag--wip' : 'jr-decision-tag--close'}`}
              title={review.closeStatus === 2
                ? 'PE will set this job to Completed (status 2) overnight — it has unbilled WIP, so it can\'t be Closed yet.'
                : 'PE will set this job to Closed (status 3) overnight.'}>
          {review.closeStatus === 2 ? 'Completing (status 2 — WIP)' : 'Closing (status 3)'}
        </span>
      ) : (
        <span className="jr-decision-tag jr-decision-tag--still"
              title="The job stays open; this is when you expect it done and why.">
          Still working · ETA {fmtDay(review.expectedCompletion)} · {reasonLabel(review.reason)}
          {review.reasonNote ? ` — ${review.reasonNote}` : ''}
        </span>
      )}
      {!review.reviewed && (
        <div className="jr-reopened" title="This row was reopened — pick a decision again to complete it.">
          Reopened — confirm your decision.
        </div>
      )}
      {review.appliedAt ? (
        <div className="jr-job-meta" title={`Applied to PE ${new Date(review.appliedAt).toLocaleString()}`}>
          Applied to PE
        </div>
      ) : (
        <div className="jr-decision-actions">
          <button className="rt-link" disabled={disabled} onClick={() => setMode('choose')}
                  title="Switch between Close now and Still working. The row stays complete.">
            Change
          </button>
          {' · '}
          <button className="rt-link" disabled={disabled} onClick={() => onUndo(row)}
                  title="Undo this decision — clears it and puts the row back to Needs decision. Available until it's applied to PE overnight.">
            Undo
          </button>
        </div>
      )}
    </div>
  );
}

// ── Forward cell ─────────────────────────────────────────────────────────────
function ForwardCell({ row, staff, readOnly, onForward }) {
  const forward = row.review?.forward || null;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);

  const disabled = readOnly;

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? staff.filter(p => (p.name || '').toLowerCase().includes(s)) : staff;
    return list.slice(0, 50);
  }, [staff, q]);

  function handleForward() {
    if (sel == null) return;
    const picked = sel;
    setOpen(false); setQ(''); setSel(null);
    onForward(row, picked);
  }

  if (open) {
    return (
      <div className="jr-form">
        <input className="jr-input" placeholder="Search staff…" value={q}
               title="Type part of a name to narrow the list (first 50 matches shown)"
               onChange={e => setQ(e.target.value)} />
        <div className="jr-staff-list">
          {matches.map(p => (
            <button key={p.staffIndex} type="button"
                    className={`jr-staff-item ${sel === p.staffIndex ? 'jr-staff-item--sel' : ''}`}
                    title={`Select ${p.name}`}
                    onClick={() => setSel(p.staffIndex)}>
              {p.name}{p.office ? <span className="jr-staff-office"> · {p.office}</span> : null}
            </button>
          ))}
          {matches.length === 0 && <div className="jr-staff-none">No staff match.</div>}
        </div>
        <div className="jr-form-actions">
          <button className="rt-btn rt-btn--primary" disabled={disabled || sel == null} onClick={handleForward}
                  title={sel == null
                    ? 'Pick a staff member first'
                    : 'Make the selected person the current staff and set the work status to Filing Late — applied in PE overnight. Doesn\'t complete the row.'}>
            Forward &amp; set Filing Late
          </button>
          <button className="rt-btn" onClick={() => setOpen(false)}
                  title="Close the picker without forwarding">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (forward) {
    return (
      <div className="jr-forwarded">
        <div title={`PE will make ${forward.staffName} the current staff and set the work status to Filing Late overnight.`}>
          &rarr; {forward.staffName} · Filing Late
        </div>
        <button className="rt-link" disabled={disabled} onClick={() => setOpen(true)}
                title="Forward to someone else instead">
          change
        </button>
        {' '}
        <button className="rt-link" disabled={disabled} onClick={() => onForward(row, null)}
                title="Withdraw this forward request (nothing changes in PE)">
          undo
        </button>
      </div>
    );
  }

  return (
    <button className="rt-btn" disabled={disabled} onClick={() => setOpen(true)}
            title="Optional: hand this job to someone else and set its work status to Filing Late (applied in PE overnight). Doesn't complete the row.">
      Forward…
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function JobReviewSection({ sectionKey }) {
  const { email, isAdmin } = useAuth();
  const { period, periodActive } = usePeriod();

  const [search, setSearch]             = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [reasonFilter, setReasonFilter]     = useState(''); // Stale only
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'pending' | 'still-working' | 'close'
  const [officeFilter, setOfficeFilter] = useState('');
  const [page, setPage]                 = useState(1);

  const fetcher = useCallback(async () => {
    if (!period || !sectionKey) return null;
    const res = await fetch(`/api/diJobReview/${period}/${sectionKey}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [period, sectionKey]);

  // The heartbeat refetches when someone else changes this section or its shared
  // snapshot (jobReviewRows / jobReviewWip) is rebuilt; `freshness` = snapshot age.
  // Optimistic saves: ordered per job, never disable the controls, and a refetch can't overwrite an edit.
  const mutations = useRowMutations(r => r.jobIdx);
  const { pinned, clearPinned } = mutations;

  const { data, setData, initialLoading, error, lastRefreshedAt, freshness, silentRefetch }
    = useSectionData(fetcher, {
      deps: [period, email, sectionKey],
      heartbeat: { sections: [sectionKey], period },
      reconcile: mutations.reconcile,
    });

  // Filters belong to one section; start clean when the section switches.
  useEffect(() => {
    setSearch(''); setCategoryFilter(''); setReasonFilter(''); setStatusFilter(''); setOfficeFilter(''); setPage(1);
  }, [sectionKey]);

  // Rows edited this session stay listed (e.g. under "Needs decision") until the user changes a filter.
  useEffect(() => { clearPinned(); }, [search, categoryFilter, reasonFilter, statusFilter, officeFilter, sectionKey, clearPinned]);

  const fireAction = payload => postJson(`/api/diJobReview/${period}/${sectionKey}`, payload);

  // Optimistic row patcher. Returns the snapshot row for revert.
  const patchRow = (jobIdx, updater) => patchRowIn(setData, jobIdx, updater);

  // Shared optimistic flow: patch -> POST (queued per job) -> revert + toast on failure.
  // Resolves true on success.
  async function postAction(row, payload, updater, okMsg) {
    const jobIdx = row.jobIdx;
    const snapshot = row;       // the rendered row = its state before this edit (a setData updater may run later)
    patchRow(jobIdx, updater);
    try {
      await mutations.run(jobIdx, () => fireAction({ ...payload, jobIdx }), {
        revert: () => { if (snapshot) patchRow(jobIdx, () => snapshot); },
        refetch: silentRefetch,
      });
      toast.success(okMsg);
      return true;
    } catch (err) {
      toast.error(`Failed: ${err.message}`, { autoClose: 10000 });
      return false;
    }
  }

  const withReview = (r, patch) => ({ ...r, review: { ...(r.review || {}), ...patch } });
  const completion = () => ({ reviewed: true, reviewedAt: new Date().toISOString(), reviewedBy: email });

  function setRecurring(row, value) {
    return postAction(
      row,
      { action: 'set-recurring', value },
      r => withReview(r, { recurring: value }),
      `Saved: rolls to next year ${value ? 'yes' : 'no'}`,
    );
  }

  // No confirm: nothing reaches PE until the overnight refresh, and the decision
  // can be changed or undone until then (a prompt per row slows down long lists).
  function closeNow(row) {
    return postAction(
      row,
      { action: 'close' },
      r => withReview(r, { disposition: 'close', closeStatus: hasWip(r) ? 2 : 3, ...completion() }),
      hasWip(row) ? 'Saved: complete (status 2 — unbilled WIP)' : 'Saved: close',
    );
  }

  function stillWorking(row, { expectedCompletion, reason, note }) {
    return postAction(
      row,
      { action: 'still-working', expectedCompletion, reason, note },
      r => withReview(r, {
        disposition: 'still-working', closeStatus: null,
        expectedCompletion, reason, reasonNote: note || null, ...completion(),
      }),
      'Saved: still working',
    );
  }

  function forwardJob(row, staffIndex) {
    const person = staffIndex == null ? null : (data?.staff || []).find(s => s.staffIndex === staffIndex);
    return postAction(
      row,
      { action: 'forward', staffIndex },
      r => withReview(r, {
        forward: person ? { staffIndex, staffName: person.name, workStatus: 'Filing Late' } : null,
      }),
      staffIndex == null ? 'Forward withdrawn' : `Forwarded to ${person ? person.name : 'staff'}`,
    );
  }

  // Undo = reset the row: decision + its details + the signoff are cleared, so it's
  // back to "needs decision". The job's manager (or an admin) can do it until the
  // overnight refresh has applied the decision to PE. The roll toggle and Forward
  // keep their own controls.
  function undoDecision(row) {
    return postAction(
      row,
      { action: 'undo' },
      r => withReview(r, {
        disposition: null, closeStatus: null, expectedCompletion: null, reason: null, reasonNote: null,
        reviewed: false, reviewedAt: null, reviewedBy: null,
      }),
      'Decision undone',
    );
  }

  // Admin override: reopens a completed row (works even after PE has applied it, when Undo can't).
  async function adminUnmark(row) {
    if (!isAdmin) return;
    if (!window.confirm(`Unmark ${row.clientCode} — ${row.jobName} as done? Whatever was already applied to PE is not changed.`)) return;
    const snapshot = row;
    patchRow(row.jobIdx, r => withReview(r, { reviewed: false, reviewedAt: null, reviewedBy: null }));
    try {
      await mutations.run(row.jobIdx, () => postAdminUnmark(period, sectionKey, row.jobIdx, 'manager'), {
        revert: () => { if (snapshot) patchRow(row.jobIdx, () => snapshot); },
        refetch: silentRefetch,
      });
      toast.success('Unmarked.');
    } catch (err) {
      toast.error(`Failed: ${err.message}`, { autoClose: 10000 });
    }
  }

  const rows    = useMemo(() => data?.rows || [], [data]);
  const reasons = useMemo(() => data?.reasons || [], [data]);
  const staff   = useMemo(() => data?.staff || [], [data]);

  const offices = useMemo(
    () => [...new Set(rows.map(r => r.jobOffice))].filter(Boolean).sort(),
    [rows]
  );

  // Late filings: only offer categories that actually appear. Stale jobs: all of them.
  const categories = useMemo(() => {
    const keys = Object.keys(CATEGORY_LABELS);
    if (sectionKey !== 'late-filings') return keys;
    const present = new Set(rows.map(r => r.category));
    return keys.filter(k => present.has(k));
  }, [rows, sectionKey]);

  // Done rows sink to the bottom, then: late filings oldest due date first, stale jobs oldest tax year first.
  const sorted = useMemo(() => {
    const byClient = (a, b) => String(a.clientCode || '').localeCompare(String(b.clientCode || ''));
    const doneLast = (a, b) => Number(!!a.review?.reviewed) - Number(!!b.review?.reviewed);
    const base = sectionKey === 'late-filings'
      ? (a, b) => {
          if (a.dueDate !== b.dueDate) {
            if (!a.dueDate) return 1;
            if (!b.dueDate) return -1;
            return a.dueDate < b.dueDate ? -1 : 1;
          }
          return byClient(a, b);
        }
      : (a, b) => {
          const ya = a.taxYear == null ? Infinity : Number(a.taxYear);
          const yb = b.taxYear == null ? Infinity : Number(b.taxYear);
          if (ya !== yb) return ya < yb ? -1 : 1;
          return byClient(a, b);
        };
    return [...rows].sort((a, b) => doneLast(a, b) || base(a, b));
  }, [rows, sectionKey]);

  const filtered = useMemo(() => {
    let out = sorted;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(r =>
        (r.clientCode || '').toLowerCase().includes(s) ||
        (r.clientName || '').toLowerCase().includes(s) ||
        (r.jobName || '').toLowerCase().includes(s)
      );
    }
    if (categoryFilter) out = out.filter(r => r.category === categoryFilter);
    if (reasonFilter) out = out.filter(r => (r.reasons || []).includes(reasonFilter));
    if (isAdmin && officeFilter) out = out.filter(r => r.jobOffice === officeFilter);
    // Rows the user just edited stay put even if the edit moved them out of this status.
    const keep = r => pinned.has(String(r.jobIdx));
    if (statusFilter === 'pending')       out = out.filter(r => keep(r) || !r.review?.reviewed);
    if (statusFilter === 'still-working') out = out.filter(r => keep(r) || (r.review?.reviewed && r.review?.disposition === 'still-working'));
    if (statusFilter === 'close')         out = out.filter(r => keep(r) || (r.review?.reviewed && r.review?.disposition === 'close'));
    return out;
  }, [sorted, search, categoryFilter, reasonFilter, officeFilter, statusFilter, isAdmin, pinned]);

  // Recomputed from rows (not data.summary) so optimistic changes move the bar.
  // Must be declared BEFORE any early returns so hook count stays stable.
  const summary = useMemo(() => {
    const total    = rows.length;
    const reviewed = rows.filter(r => r.review?.reviewed).length;
    return { total, reviewed, pending: total - reviewed };
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // data from the previous section can linger for a render after a switch.
  if (initialLoading || (data && data.sectionKey !== sectionKey)) {
    return <div className="rt-loading">Loading jobs…</div>;
  }
  if (error && !data) return <div className="rt-error">Error: {error}</div>;
  if (!data) return null;

  const readOnly = !periodActive;
  const mirror   = data.mirror;
  const label    = data.label || 'Jobs';
  const pct = summary.total > 0 ? Math.round((summary.reviewed / summary.total) * 100) : 0;

  return (
    <div className="rt-page">
      <div className="rt-header">
        <div className="rt-title-block">
          <h2 className="rt-title">{label}</h2>
          {readOnly && <span className="rt-readonly-badge">Read-only</span>}
          <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
        </div>
      </div>
      <div className="jr-intro">
        <div className="jr-legend">
          <div><strong>Rolls to next year</strong> — PE&rsquo;s &ldquo;Recurring&rdquo; setting: whether closing this job creates next year&rsquo;s job. It&rsquo;s separate from whether you close the job now.</div>
          <div><strong>Decision</strong> — Close now, or Still working (expected completion + reason). Picking one completes the row. Changes are applied to PE overnight.</div>
        </div>
        <p className="rt-subtitle">{SUBTITLES[sectionKey]}</p>
      </div>
      <MirrorNote mirror={mirror} />

      <div className="rt-progress-section">
        <div className="rt-progress-header">
          <h3 className="rt-progress-title">{isAdmin ? 'Firm Progress' : 'Your Progress'}</h3>
          <span className="rt-progress-label-val">
            <span className="rt-progress-count">{summary.reviewed} / {summary.total}</span>
            <span className="rt-progress-divider">—</span>
            {pct}% Complete
          </span>
        </div>
        <div className="rt-progress-track">
          <div
            className="rt-progress-fill"
            style={{
              width: `${Math.min(100, Math.max(0, pct))}%`,
              background: pct >= 100 ? '#1a7a4a' : pct >= 50 ? '#e6a817' : '#c0392b',
            }}
          />
        </div>
      </div>

      <div className="rt-summary">
        <div className="rt-stat"><span className="rt-stat-val">{summary.total}</span><span className="rt-stat-lbl">{isAdmin ? 'Jobs' : 'Your Jobs'}</span></div>
        <div className="rt-stat rt-stat--pending"><span className="rt-stat-val">{summary.pending}</span><span className="rt-stat-lbl">Needs Decision</span></div>
        <div className="rt-stat rt-stat--done"><span className="rt-stat-val">{summary.reviewed}</span><span className="rt-stat-lbl">Done</span></div>
      </div>

      <div className="rt-filters">
        <input className="rt-filter-input" placeholder="Search by client code, client name, or job…"
               value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="rt-filter-select" value={categoryFilter}
                onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
        </select>
        {sectionKey === 'stale-jobs' && (
          <select className="rt-filter-select" value={reasonFilter} title="Why the job is listed"
                  onChange={e => { setReasonFilter(e.target.value); setPage(1); }}>
            <option value="">All Reasons</option>
            {Object.entries(REASONS).map(([code, r]) => <option key={code} value={code}>{r.label}</option>)}
          </select>
        )}
        <select className="rt-filter-select" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="pending">Needs decision</option>
          <option value="still-working">Still working</option>
          <option value="close">Closing</option>
        </select>
        {isAdmin && (
          <select className="rt-filter-select" value={officeFilter}
                  onChange={e => { setOfficeFilter(e.target.value); setPage(1); }}>
            <option value="">All Offices</option>
            {offices.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
      </div>

      <div className="rt-table-wrap">
        <table className="rt-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Job</th>
              <th>Status</th>
              <th>Time</th>
              <th title="Job Partner (admins also see the Job Manager)">Job Partner</th>
              <th title="PE's Recurring setting: whether closing this job creates next year's job. Separate from closing it now.">
                Rolls to next year
              </th>
              <th title="Close now, or Still working (expected completion + reason). Picking one completes the row.">
                Decision
              </th>
              <th title="Optional: hand the job to someone else and set it to Filing Late.">Forward</th>
              <th title="Done once a decision is recorded.">Done</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(row => {
              const review   = row.review || {};
              const reviewed = !!review.reviewed;
              const roll     = row.roll || {};
              const reviewedTip = reviewed ? doneTip({ by: review.reviewedBy, at: review.reviewedAt }) : undefined;
              const rollChecked = review.recurring ?? roll.peRecurring;
              const rollChanged = review.recurring != null && review.recurring !== roll.peRecurring;

              return (
                <tr key={row.jobIdx} className={reviewed ? 'rt-row-reviewed' : ''}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{row.clientCode}</div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>{row.clientName}</div>
                  </td>
                  <td>
                    <div className="jr-jobname">{row.jobName}</div>
                    <div className="jr-job-meta">
                      TY {row.taxYear ?? '—'} · {CATEGORY_LABELS[row.category] || row.category}
                      {row.dueDate ? ` · due ${fmtDay(row.dueDate)}` : ''}
                      {row.idd ? ` · IDD ${fmtDay(row.idd)}` : ''}
                    </div>
                    {(row.reasons || []).length > 0 && (
                      <div className="jr-reasons">{row.reasons.map(code => reasonChip(code, row, data.taxYear))}</div>
                    )}
                    <PeJobLink jobIdx={row.jobIdx} />
                  </td>
                  <td>
                    <div>{row.workStatus || <span className="rt-cell-muted">—</span>}</div>
                    {row.currentStaff && <div className="jr-job-meta">Staff: {row.currentStaff}</div>}
                  </td>
                  <td>
                    <div>{fmtHours(row.hours)} h</div>
                    <div className="jr-job-meta">
                      {row.lastTimeEntry ? `last ${fmtDate(row.lastTimeEntry)}` : 'no time'}
                    </div>
                    {hasWip(row) && <div className="jr-wip">Unbilled WIP {formatMoney(row.unbilledWip, { cents: true })}</div>}
                  </td>
                  <td>
                    <div>{row.jobPartner || <span className="rt-cell-muted">—</span>}</div>
                    {isAdmin && row.jobManager && <div className="jr-job-meta">JM: {row.jobManager}</div>}
                  </td>
                  <td>
                    {roll.state === 'toggle' && (
                      <div className="jr-roll"
                           title={rollChecked
                             ? 'Yes — closing this job will create next year\'s job. Click to turn rolling off (e.g. the client is leaving or won\'t need this next year). Applied in PE overnight.'
                             : 'No — closing this job will NOT create next year\'s job. Click to turn rolling on. Applied in PE overnight.'}>
                        <ToggleSwitch
                          checked={!!rollChecked}
                          disabled={readOnly}
                          onChange={checked => setRecurring(row, checked)}
                        />
                        <div className="jr-job-meta">{rollChecked ? 'Yes' : 'No'}</div>
                        {rollChanged && (
                          <span className="jr-changed"
                                title={`PE currently says ${roll.peRecurring ? 'Yes' : 'No'}; your change is applied overnight.`}>
                            changed — applies overnight
                          </span>
                        )}
                      </div>
                    )}
                    {roll.state === 'already-rolled' && (
                      <div>
                        <span className="jr-badge"
                              title={`Next year's job already exists (${roll.nextJobName || 'unknown'}), so there's nothing to roll.`}>
                          Already rolled
                        </span>
                        {roll.nextJobName && <div className="jr-job-meta">{roll.nextJobName}</div>}
                      </div>
                    )}
                    {roll.state === 'prior-year' && (
                      <span className="jr-badge" title="This job is from a prior tax year, so closing it does not create a next-year job.">
                        Prior year — closes without rolling
                      </span>
                    )}
                  </td>
                  <td>
                    <DecisionCell
                      row={row}
                      reasons={reasons}
                      readOnly={readOnly}
                      onClose={closeNow}
                      onStill={stillWorking}
                      onUndo={undoDecision}
                    />
                  </td>
                  <td>
                    <ForwardCell
                      row={row}
                      staff={staff}
                      readOnly={readOnly}
                      onForward={forwardJob}
                    />
                  </td>
                  <td>
                    <DoneCell done={reviewed} tip={reviewedTip} isAdmin={isAdmin}
                              readOnly={readOnly} onUnmark={() => adminUnmark(row)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {summary.total === 0 && (
          <div className="rt-empty">Nothing to review in {label}.</div>
        )}
        {filtered.length === 0 && summary.total > 0 && (
          <div className="rt-empty">No jobs match.</div>
        )}
      </div>

      <ReviewPagination page={safePage} totalPages={totalPages} rowCount={filtered.length} onPage={setPage} />
    </div>
  );
}
